import { useCallback, useEffect, useMemo, useState } from 'react'
import { vclog } from './vc-log'
import {
  buildWhiteboardUrl,
  deriveBoardKey,
  whiteboardConfigured,
} from '../services/whiteboard-url'

/**
 * The room's shared whiteboard.
 *
 * The wire protocol is deliberately tiny: `whiteboard:started {by, startedAt}`
 * and `whiteboard:stopped {by}`. No URL travels between participants — this
 * hook derives the board from the room id and frames it with the local user's
 * own name and edit rights (see services/whiteboard-url.ts for why).
 *
 * Replaces the local canvas whiteboard, which drew to nobody: every participant
 * had their own private surface and no two of them ever saw the same thing.
 */

type WhiteboardState = {
  active: boolean
  /** peerId of whoever opened it. */
  by: string | null
  startedAt: number | null
}

type Args = {
  peerId: string | null
  roomId: string | null
  displayName: string
  /** May this participant draw? Attendees without a presenter grant may not. */
  canEdit: boolean
  /** May this participant open a board at all (presenter capability)? */
  canPresent: boolean
  /** Moderators may close anyone's board; the opener may close their own. */
  isModerator: boolean
  // Same shape as the meeting's `ChatChannel` handles, so the room channel can
  // be passed straight in.
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  subscribe: (event: string, handler: (payload: unknown) => void) => () => void
  ready: boolean
}

const IDLE: WhiteboardState = { active: false, by: null, startedAt: null }

export function useWhiteboard({
  peerId,
  roomId,
  displayName,
  canEdit,
  canPresent,
  isModerator,
  sendEvent,
  subscribe,
  ready,
}: Args) {
  const [state, setState] = useState<WhiteboardState>(IDLE)
  const [boardKey, setBoardKey] = useState<string | null>(null)

  // Derived once per room. WebCrypto is async, so this cannot happen inline
  // during render — and it must not be recomputed per frame either.
  useEffect(() => {
    if (!roomId) {
      setBoardKey(null)
      return
    }
    let cancelled = false
    void deriveBoardKey(roomId).then((key) => {
      if (!cancelled) setBoardKey(key)
    })
    return () => {
      cancelled = true
    }
  }, [roomId])

  // Room state. `whiteboard:started` also arrives on join when a board is
  // already open, so a late joiner lands in it rather than missing it.
  useEffect(() => {
    if (!ready) return
    const offs = [
      subscribe('whiteboard:started', (payload) => {
        const p = payload as { by?: string; startedAt?: number }
        vclog('presence', 'whiteboard started', { by: p?.by })
        setState({ active: true, by: p?.by ?? null, startedAt: p?.startedAt ?? Date.now() })
      }),
      subscribe('whiteboard:stopped', (payload) => {
        const p = payload as { by?: string }
        vclog('presence', 'whiteboard stopped', { by: p?.by })
        setState(IDLE)
      }),
      /**
       * A board cannot outlive whoever opened it.
       *
       * The room's own copy is cleaned server-side when the session ends, which
       * is what stops a later joiner being replayed a ghost. This is the local
       * half of the same rule — the same one `useSharedMedia` applies to the
       * media stack — so everyone still in the meeting drops the board the
       * moment the owner is known gone, rather than waiting for a `stop` that a
       * refresh, a dropped connection or a moderator removal never sends.
       *
       * Closing a board that is already closed is a no-op, so the two halves
       * cannot fight.
       */
      subscribe('media:peer_left', (payload) => {
        const gone = (payload as { peerId?: unknown } | null)?.peerId
        if (typeof gone !== 'string') return
        setState((prev) => {
          if (!prev.active || prev.by !== gone) return prev
          vclog('presence', 'whiteboard owner left — closing the board', { by: gone })
          return IDLE
        })
      }),
    ]
    return () => offs.forEach((off) => off())
  }, [ready, subscribe])

  /**
   * Open the board. Returns why it refused, or null if it went ahead.
   *
   * One board per room, and it belongs to whoever opened it. Someone else's
   * being up is checked HERE rather than only at the server because `sendEvent`
   * is fire-and-forget — a Phoenix error reply has nowhere to arrive — and
   * because this client already knows: the board's owner came with
   * `whiteboard:started`. The server refuses the same case anyway, as the
   * backstop for a race between two people clicking at once.
   *
   * Nothing is lost by refusing: a board is room-wide, so the person who
   * clicked is already looking at it.
   */
  const start = useCallback((): 'not_presenter' | 'already_open' | null => {
    if (!canPresent) return 'not_presenter'
    if (state.active && state.by && state.by !== peerId) return 'already_open'
    sendEvent('whiteboard:start', {})
    return null
  }, [canPresent, sendEvent, state.active, state.by, peerId])

  const stop = useCallback(() => {
    sendEvent('whiteboard:stop', {})
  }, [sendEvent])

  const isPresenter = state.active && !!peerId && state.by === peerId

  /** The URL THIS client frames. Empty until the key resolves or if unconfigured. */
  const url = useMemo(() => {
    if (!state.active || !roomId || !boardKey) return ''
    return buildWhiteboardUrl(boardKey, { roomId, displayName, canEdit })
  }, [state.active, roomId, boardKey, displayName, canEdit])

  return {
    active: state.active,
    startedBy: state.by,
    startedAt: state.startedAt,
    url,
    /** This client opened the board that is up. */
    isPresenter,
    /** Opener or moderator — the two who may close it. */
    canClose: state.active && (isModerator || isPresenter),
    canPresent,
    /** Whether this client's board loads with drawing tools. */
    canEdit,
    configured: whiteboardConfigured(),
    start,
    stop,
  }
}
